import {
  getAboveNode,
  getNextNodeStartPoint,
  getPreviousNodeEndPoint,
  insertNodes,
  isCollapsed,
  isEndPoint,
  isStartPoint,
  isType,
  mockPlugin,
  PlateEditor,
  select,
  Value,
} from '@udecode/plate-common';
import { withRemoveEmptyNodes } from '@udecode/plate-normalizers';
// eslint-disable-next-line @typescript-eslint/no-shadow
import { Path, Point, Range } from 'slate';

import { ELEMENT_ANCHOR_LINK, MyText } from '../../plateTypes';
import { ELEMENT_INTERLINK } from './createInterlinkPlugin';

// NOTE: url links are handled by the link plugin from Plate.
export const withMyLinks = <
  V extends Value = Value,
  E extends PlateEditor<V> = PlateEditor<V>,
>(
  editor: E,
): E => {
  const { apply, normalizeNode } = editor;

  editor.apply = (operation) => {
    if (operation.type === 'set_selection') {
      const range = operation.newProperties as Range | null;

      if (range && range.focus && range.anchor && isCollapsed(range)) {
        const entry = getAboveNode(editor, {
          at: range,
          match: (node) =>
            isType(editor, node, [ELEMENT_INTERLINK, ELEMENT_ANCHOR_LINK]),
        });

        if (entry) {
          const [, path] = entry;

          let newPoint: Point | undefined;

          if (isStartPoint(editor, range.focus, path)) {
            newPoint = getPreviousNodeEndPoint(editor, path);
          }

          if (isEndPoint(editor, range.focus, path)) {
            newPoint = getNextNodeStartPoint(editor, path);
          }

          if (newPoint) {
            operation.newProperties = {
              anchor: newPoint,
              focus: newPoint,
            };
          }
        }
      }
    }

    apply(operation);
  };

  editor.normalizeNode = ([node, path]) => {
    if (isType(editor, node, [ELEMENT_INTERLINK, ELEMENT_ANCHOR_LINK])) {
      const range = editor.selection as Range | null;

      if (
        range &&
        isCollapsed(range) &&
        isEndPoint(editor, range.focus, path)
      ) {
        const nextPoint = getNextNodeStartPoint(editor, path);

        // select next text node if any
        if (nextPoint) {
          select(editor, nextPoint);
        } else {
          // insert text node then select
          const nextPath = Path.next(path);
          insertNodes<MyText>(editor, { text: '' } as MyText, { at: nextPath });
          select(editor, nextPath);
        }
      }
    }

    normalizeNode([node, path]);
  };

  editor = withRemoveEmptyNodes<V, E>(
    editor,
    mockPlugin<object, V, E>({
      options: { types: [ELEMENT_INTERLINK, ELEMENT_ANCHOR_LINK] },
    }),
  );

  return editor;
};
